import { useEffect, useMemo, useState, useRef } from "react";
import ReportTable from "../ReportTable.component";
import BaseReport from "../BaseReport.component";
import ReportModal from "../ReportModal.component";
import ReportService from "../../../services/reports/Report.service";
import { InventoryHealthRawData, InventoryHealthParsedData } from "../../../interfaces/reports/InventoryHealthReport.interface";
import { MoneyDataItem, PercentDataItem, StringDataItem } from "../../../interfaces/reports/BaseReport.interface";
import TipContext from "../../../providers/GlobalTip";
import EpochMonthService from "../../../services/EpochMonth.service";

const TODAY_EPOCH_MONTH = EpochMonthService.getTodayEpochMonth();

interface InventoryHealthReportProps {
    clientId: string | number;
    epochMonth: number | null;
}

const HIERARCHY_OPTIONS: Record<string, string[]> = {
    "By Location": ["locationCode"],
    "Location By Class": ["locationCode", "classCode"],
    "Location By Category": ["locationCode", "category"],
    "Location by Category by Class": ["locationCode", "category", "classCode"],
    "By Class": ["classCode"],
    "Class by Location": ["classCode", "locationCode"],
    "By Category": ["category"],
    "Category by Location": ["category", "locationCode"],
    "Category by Class": ["category", "classCode"],
    "Category by Class by Location": ["category", "classCode", "locationCode"],
    "Class by Child": ["parentClientClassCode", "classCode"],
    "Class by Child by Location": ["parentClientClassCode", "classCode", "locationCode"],
    "Location by Class by Child": ["locationCode", "parentClientClassCode", "classCode"],
    "Category by Class by Child": ["category", "parentClientClassCode", "classCode"],
};

const processRawData = (rawData: InventoryHealthRawData[]): InventoryHealthParsedData[] => {
    if (!rawData || rawData.length === 0) return [];

    return rawData.map((item) => ({
        locationCode: item.locationCode,
        locationName: item.locationName,
        classCode: item.classCode,
        className: item.className,
        category: item.category,
        parentClientClassCode: item.parentClientClassCode ?? null,

        salesTwoMonthsAgo: new MoneyDataItem(item.salesTwoMonthsAgo),
        salesLastMonth: new MoneyDataItem(item.salesLastMonth),
        salesThisMonth: new MoneyDataItem(item.salesThisMonth),
        inventoryThisMonth: new MoneyDataItem(item.inventoryThisMonth),
        inventoryBomPlanNextMonth: new MoneyDataItem(item.inventoryBomPlanNextMonth),
        otb: new MoneyDataItem(item.otb),

        wos: new StringDataItem(0),
        vsPlanPercent: new PercentDataItem(0),
        health: new StringDataItem(""),
    }));
};

const reapplyHealthCalcs = (data: InventoryHealthParsedData[]): InventoryHealthParsedData[] => {
    return data.map((item) => {
        const actualInventory = Number(item.inventoryThisMonth?.value) || 0;
        const planInventory = Number(item.inventoryBomPlanNextMonth?.value) || 0;
        const avgWeeklySales = ((Number(item.salesTwoMonthsAgo?.value) || 0) + (Number(item.salesLastMonth?.value) || 0)) / 9;


        const wos = avgWeeklySales > 0 ? Math.round((actualInventory / avgWeeklySales) * 10) / 10 : 0;
        const vsPlanPercent = planInventory > 0
            ? Math.round(((actualInventory - planInventory) / planInventory) * 100)
            : 0;
        const health = Math.abs(vsPlanPercent) <= 10 ? "Healthy" : Math.abs(vsPlanPercent) <= 20 ? "Watch" : "Critical";

        return {
            ...item,
            wos: new StringDataItem(wos),
            vsPlanPercent: new PercentDataItem(vsPlanPercent),
            health: new StringDataItem(health),
        };
    });
};

function HealthBadge(value: string | number) {
    const rating = String(value);
    const styles: Record<string, string> = {
        Healthy: "bg-emerald-100 text-emerald-700",
        Watch: "bg-amber-100 text-amber-700",
        Critical: "bg-red-100 text-red-700",
    };
    return (
        <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${styles[rating] ?? "bg-gray-100 text-gray-700"}`}>
            {rating}
        </span>
    );
}

const InventoryHealthReport = ({ clientId, epochMonth }: InventoryHealthReportProps) => {
    const globalTip = TipContext.useMessage();
    const dataRef = useRef<InventoryHealthRawData[]>([]);
    const parentDataRef = useRef<InventoryHealthRawData[]>([]);
    const [parsedData, setParsedData] = useState<InventoryHealthParsedData[]>([]);
    const [parentParsedData, setParentParsedData] = useState<InventoryHealthParsedData[]>([]);
    const [sortedData, setSortedData] = useState<InventoryHealthParsedData[]>([]);
    const [loading, setLoading] = useState(true);
    const [hierarchy, setHierarchy] = useState<string[] | null>(["classCode"]);
    const [wosTarget, setWosTarget] = useState<number>(6);

    const [locationFilter, setLocationFilter] = useState<string[]>([]);
    const [classFilter, setClassFilter] = useState<string[]>([]);
    const [categoryFilter, setCategoryFilter] = useState<string[]>([]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            ReportService.getInventoryHealthReportData({ clientId, epochMonth, includeChildClasses: true }).then((response) => {
                if (response) {
                    dataRef.current = response.data ?? [];
                    parentDataRef.current = response.parentLevelData ?? [];

                    const processed = processRawData(response.data ?? []);
                    const processedParent = processRawData(response.parentLevelData ?? []);
                    setParsedData(processed);
                    setParentParsedData(processedParent);

                    const useParent = hierarchy?.includes("parentClientClassCode");
                    let sorted = ReportService.groupByHierarchy(useParent ? processedParent : processed, hierarchy ?? ["classCode"]);
                    sorted = reapplyHealthCalcs(sorted);
                    setSortedData(sorted);
                } else {
                    dataRef.current = [];
                    parentDataRef.current = [];
                    setParsedData([]);
                    setParentParsedData([]);
                    setSortedData([]);
                }
                setLoading(false);
            }).catch(() => {
                if (globalTip) globalTip.show("Failed to fetch data");
                setLoading(false);
            });
        };
        fetchData();
    }, [clientId, epochMonth]);

    useEffect(() => {
        const useParent = hierarchy?.includes("parentClientClassCode");
        const sourceData = useParent ? parentParsedData : parsedData;

        if (sourceData.length > 0) {
            let newSortedData = ReportService.sortAndFilterData(
                sourceData,
                hierarchy ?? ["classCode"],
                locationFilter,
                classFilter,
                categoryFilter
            );
            newSortedData = reapplyHealthCalcs(newSortedData);
            setSortedData(newSortedData);
        }
    }, [hierarchy, parsedData, parentParsedData, locationFilter, classFilter, categoryFilter]);

    let hierarchyColumns = [];
    for (let i = 0; i < (hierarchy?.length ?? 0); i++) {
        hierarchyColumns.push({
            name: "",
            sortable: false,
            customClassName: i === 0
                ? "px-4 py-2 bg-gray-500 text-white"
                : "px-4 py-2 bg-purple-100 border-purple-700 text-purple-800"
        });
    }

    const wosCustomFormatting = (value: string | number) => {
        const wosVal = Number(value);
        if (isNaN(wosVal)) return <span>{value}</span>;
        let colorClass = "text-emerald-600";
        if (wosTarget > 0) {
            if (wosVal < wosTarget * 0.7) colorClass = "text-red-600";
            else if (wosVal > wosTarget * 1.3) colorClass = "text-amber-600";
        }
        return <span className={`font-semibold ${colorClass}`}>{wosVal}</span>;
    };

    const columns = [
        ...hierarchyColumns,
        { name: "Sales", sortable: true },
        { name: "Inventory", sortable: true },
        { name: "Inventory Plan", sortable: true },
        { name: "Vs Plan %", sortable: true },
        { name: "WOS", sortable: true, customFormatting: wosCustomFormatting },
        { name: "OTB", sortable: true },
        { name: "Health", sortable: true, customFormatting: HealthBadge },
    ];

    const tableData = useMemo(() => {
        return sortedData.map((item) => {
            const labelRows = ReportService.hierarchyItemToVerticalDisplayNames(item, hierarchy ?? ["classCode"]);
            return [
                ...labelRows,
                item.salesThisMonth?.formatted,
                item.inventoryThisMonth?.formatted,
                item.inventoryBomPlanNextMonth?.formatted,
                item.vsPlanPercent?.formatted,
                item.wos?.formatted,
                item.otb?.formatted,
                item.health?.formatted,
            ];
        });
    }, [sortedData, hierarchy]);

    const rowClassName = (rowIndex: number, sortedData?: Array<Array<string | number>>) => {
        const source = sortedData ?? tableData;
        if (!source || rowIndex < 0 || rowIndex >= source.length) return null;
        return ReportService.getRowClassName(source[rowIndex], hierarchy ?? ["classCode"]);
    };

    const nonNullEpochMonth = epochMonth ?? TODAY_EPOCH_MONTH;

    return (
        <div className="w-full h-full flex flex-col items-center justify-center text-center">
            <h2 className="text-2xl font-bold mb-4">{`Inventory Health Report - ${EpochMonthService.epochMonthToMMMYY(nonNullEpochMonth)}`}</h2>
            {loading ? (
                <p>Loading...</p>
            ) : (
                <div className="h-full max-w-[1400px]">
                    <BaseReport
                        title="Inventory Health Report"
                        tableColumns={columns.map((col) => col.name)}
                        tableData={tableData}
                        fileName={`Inventory_Health_Report_${new Date().toLocaleDateString()}`}
                        setHierarchy={setHierarchy}
                        hierarchy={hierarchy}
                        hierarchyOptions={HIERARCHY_OPTIONS}

                        parsedData={parsedData}
                        filteredLocationCodes={locationFilter}
                        filteredClassCodes={classFilter}
                        setFilteredLocationCodes={setLocationFilter}
                        setFilteredClassCodes={setClassFilter}
                        filteredCategories={categoryFilter}
                        setFilteredCategories={setCategoryFilter}

                        onApplyFilters={() => {
                            const processed = processRawData(dataRef.current);
                            setParsedData(processed);
                        }}
                    />

                    <div className="flex flex-row flex-wrap items-center space-x-4 mb-4 ml-8">
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">WOS Target (Weeks)</span>
                            <input
                                type="number"
                                value={wosTarget}
                                onChange={e => setWosTarget(Number(e.target.value) || 0)}
                                min={0}
                                step={0.5}
                                className="h-10 w-32 px-4 border border-[#e1edf1] rounded-md text-[13px] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-200"
                            />
                        </div>
                    </div>

                    <ReportTable
                        columns={columns}
                        data={tableData}
                        rowClassName={rowClassName}
                        defaultSortColumn="Sales"
                        defaultSortDirection="desc"
                        headerClassName="bg-slate-700 !text-white !text-center py-2 px-4"
                        toPdf={() => (window as any).BaseReportExport && (window as any).BaseReportExport('pdf')}
                        toCsv={() => (window as any).BaseReportExport && (window as any).BaseReportExport('csv')}
                        stickyHeader={true}
                        hierarchyDepth={hierarchy ? hierarchy.length : 0}
                    />
                </div>
            )}
        </div>
    );
};

interface InventoryHealthReportModalProps {
    clientId: string | number;
    epochMonth: number | null;
    onClose: () => void;
}

const InventoryHealthReportModal = ({ clientId, epochMonth, onClose }: InventoryHealthReportModalProps) => {
    return (
        <ReportModal
            onClose={onClose}
        >
            <InventoryHealthReport clientId={clientId} epochMonth={epochMonth} />
        </ReportModal>
    );
};

export { InventoryHealthReport, InventoryHealthReportModal };

